import { statusLabel } from '../CargoClaims/statuses';
import { CLAIM_STATUSES, SetBy } from './cargo-docs.data';
import '../ListZeroWorkflow/list-zero-workflow.scss';

/*
 * What each claim status means and who sets it.
 *
 * The labels come from `statusLabel`, the same function the claims grid uses,
 * so a status renamed on the screen cannot quietly disagree with the page that
 * explains it.
 */

const SET_BY_LABEL: Record<SetBy, string> = {
  azadea: 'AZADEA',
  clover: 'Clover',
  system: 'Automatic',
};

function SetByBadges({ setBy }: { setBy: SetBy[] }) {
  return (
    <span className="type-badges">
      {setBy.map((who) => (
        <span key={who} className={`type-badge type-badge--${who}`}>
          {SET_BY_LABEL[who]}
        </span>
      ))}
    </span>
  );
}

export default function CargoClaimsStatuses() {
  return (
    <div className="list-zero-workflow">
      <section className="workflow-card">
        <h2>Claim Statuses</h2>
        <p>
          A claim has one status at a time, and it can only move along the paths
          below. Anything else is refused, so a claim cannot skip the paperwork
          or be closed without a credit note.
        </p>
        <p>
          Closed and Cancelled are the two ends. Nothing moves out of them, which
          is what makes a closed claim mean something.
        </p>
      </section>

      <section className="workflow-card">
        <table className="workflow-table status-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Status</th>
              <th>What it means</th>
              <th style={{ width: 170 }}>Set by</th>
              <th style={{ width: 210 }}>Can become</th>
            </tr>
          </thead>
          <tbody>
            {CLAIM_STATUSES.map((row) => (
              <tr key={row.status} className={row.terminal ? 'status-row--unused' : undefined}>
                <td>
                  <div className="fields-name">{statusLabel(row.status)}</div>
                  {row.terminal && <span className="fields-tag">Final</span>}
                </td>
                <td>{row.meaning}</td>
                <td>
                  <SetByBadges setBy={row.setBy} />
                </td>
                <td className="fields-applies">{row.next}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="workflow-card">
        <h2>In review</h2>
        <div className="workflow-callout">
          AZADEA's own system has no status matching <strong>In review</strong>,
          so a claim Clover is working on continues to report to them as
          Documented. That stays true, because Clover holds the documents and
          has not decided yet, and it means changes to the way Clover works do
          not require a change on their side. On this screen the claim is shown as
          "In review by Clover", so it does not read as though nothing is
          happening.
        </div>
      </section>

      <section className="workflow-card">
        <h2>Cancelled</h2>
        <p>
          A claim can be cancelled from any status before it closes, and there are
          three ways it happens:
        </p>
        <ul>
          <li>
            <strong>AZADEA withdraws it.</strong> Recorded against them, with the
            reason they gave.
          </li>
          <li>
            <strong>Clover cancels it.</strong> The same outcome for the claim;
            the difference is only in who is recorded as having done it.
          </li>
          <li>
            <strong>The documents never arrived.</strong> A claim still at Received
            90 days after it was filed is cancelled overnight, recorded as
            automatic rather than against a person.
          </li>
        </ul>
        <p className="status-footnote">
          A cancellation reason is kept separately from a decision reason, because
          giving up on a claim is not a judgement on whether it was valid.
        </p>
      </section>
    </div>
  );
}
